/* eslint-disable @typescript-eslint/naming-convention */

import classNames from 'classnames';
import {createUltimatePagination, ITEM_TYPES} from 'components/PaginationBasic';
import {Typography} from 'components/Typography';
import {PageInfo} from 'models';
import React, {FC} from 'react';
import style from './style.module.scss';
import Icon from 'components/Icon';
import {useRouter} from 'next/router';
import Link from 'next/link';
import {PAGINATION_PARAM} from 'core/constants';

export interface IPaginationProps {
    value: PageInfo['pageNumber'];
    isActive: boolean;
    onClick?: () => void;
    isDisabled: boolean;
}

const Page: FC<IPaginationProps> = ({value, isActive, isDisabled}) => {
    const {pathname, query} = useRouter();

    const queryWithoutPageNumber = Object.fromEntries(
        Object.entries(query).filter(([key]) => {
            return 'page-number' !== key;
        }),
    );

    return (
        <Link
            className={classNames(style.pageButton, {
                [style.isDisabled]: isDisabled,
                [style.pageButtonActive]: isActive,
            })}
            href={{
                pathname,
                query: 1 !== value ? {...query, [PAGINATION_PARAM]: value} : {...queryWithoutPageNumber},
            }}
        >
            <Typography element={'span'} variant={'label-14-m'}>
                {value}
            </Typography>
        </Link>
    );
};

const Ellipsis: FC<IPaginationProps> = ({isDisabled, value}) => {
    const {pathname, query} = useRouter();

    return (
        <Link
            className={classNames(style.pageButton, style.pageButtonEllipsis, {
                [style.isDisabled]: isDisabled,
            })}
            href={{
                pathname,
                query: {...query, [PAGINATION_PARAM]: value},
            }}
        >
            <Typography element={'span'} variant={'label-14-m'}>
                ...
            </Typography>
        </Link>
    );
};

const FirstPageLink: FC<IPaginationProps> = () => null;

const PreviousPageLink: FC<IPaginationProps> = ({isDisabled, value}) => {
    const {pathname, query} = useRouter();
    const queryWithoutPageNumber = Object.fromEntries(
        Object.entries(query).filter(([key]) => {
            return 'page-number' !== key;
        }),
    );

    return (
        <Link
            className={classNames(style.pageButton, {
                [style.isDisabled]: isDisabled,
            })}
            href={{
                pathname,
                query: 1 !== value ? {...query, [PAGINATION_PARAM]: value} : {...queryWithoutPageNumber},
            }}
        >
            <Icon className={classNames(style.arrow, style.arrowPrev)} icon={'pagination-chevron'} />
        </Link>
    );
};

const NextPageLink: FC<IPaginationProps> = ({isDisabled, value}) => {
    const {pathname, query} = useRouter();

    return (
        <Link
            className={classNames(style.pageButton, {
                [style.isDisabled]: isDisabled,
            })}
            href={{
                pathname,
                query: {...query, [PAGINATION_PARAM]: value},
            }}
        >
            <Icon className={classNames(style.arrow, style.arrowNext)} icon={'pagination-chevron'} />
        </Link>
    );
};

const LastPageLink: FC<IPaginationProps> = () => null;

const itemTypeToComponent = {
    [ITEM_TYPES.PAGE]: Page,
    [ITEM_TYPES.ELLIPSIS]: Ellipsis,
    [ITEM_TYPES.FIRST_PAGE_LINK]: FirstPageLink,
    [ITEM_TYPES.PREVIOUS_PAGE_LINK]: PreviousPageLink,
    [ITEM_TYPES.NEXT_PAGE_LINK]: NextPageLink,
    [ITEM_TYPES.LAST_PAGE_LINK]: LastPageLink,
};

export const Pagination = createUltimatePagination({itemTypeToComponent});
